import { BaseItem, NullablePartial } from '../common';

export enum PointReference {
  LeftTop = 'LEFT_TOP',
  LeftBottom = 'LEFT_BOTTOM',
  RightTop = 'RIGHT_TOP',
  RightBottom = 'RIGHT_BOTTOM',
}

export enum DataSource {
  Crossmark = 'CROSSMARK',
  DateSigned = 'DATE_SIGNED',
  GuardianRelationship = 'GUARDIAN_RELATIONSHIP',
  MRN = 'MRN', // deprecated use PATIENT_MRN
  Signature = 'SIGNATURE',

  GuarantorAge = 'GUARANTOR_AGE',
  GuarantorDOB = 'GUARANTOR_DOB',
  GuarantorHomeAddress = 'GUARANTOR_HOME_ADDRESS',
  GuarantorHomeCityStateZip = 'GUARANTOR_HOME_CITY_STATE_ZIP',
  GuarantorHomePhoneNumber = 'GUARANTOR_HOME_PHONE_NUMBER',
  GuarantorName = 'GUARANTOR_NAME',
  GuarantorRelationship = 'GUARANTOR_RELATIONSHIP', // deprecated use GUARANTOR_RELATIONSHIP_TO_PATIENT
  GuarantorRelationshipToPatient = 'GUARANTOR_RELATIONSHIP_TO_PATIENT',
  GuarantorSex = 'GUARANTOR_SEX',
  GuarantorSSN = 'GUARANTOR_SSN',

  PatientAge = 'PATIENT_AGE',
  NewbornAge = 'NEWBORN_AGE',
  PatientBirthGestationalAge = 'PATIENT_BIRTH_GESTATIONAL_AGE',
  PatientBirthWeightGrams = 'PATIENT_BIRTH_WEIGHT_GRAMS',
  PatientDOB = 'PATIENT_DOB',
  PatientHomeAddress = 'PATIENT_HOME_ADDRESS',
  PatientHomeCityStateZip = 'PATIENT_HOME_CITY_STATE_ZIP',
  PatientHomePhoneNumber = 'PATIENT_HOME_PHONE_NUMBER',
  PatientInitials = 'PATIENT_INITIALS',
  PatientIP = 'PATIENT_IP',
  PatientMobilePhoneNumber = 'PATIENT_MOBILE_PHONE_NUMBER',
  PatientMotherName = 'PATIENT_MOTHER_NAME',
  PatientMRN = 'PATIENT_MRN',
  PatientAthenaIdxMrn = 'PATIENT_ATHENA_IDX_MRN',
  PatientName = 'PATIENT_NAME',
  PatientRace = 'PATIENT_RACE',
  PatientSex = 'PATIENT_SEX',
  PatientSSN = 'PATIENT_SSN',

  PrimaryInsuranceGroupNumber = 'PRIMARY_INSURANCE_GROUP_NUMBER',
  PrimaryInsuranceInsuranceName = 'PRIMARY_INSURANCE_INSURANCE_NAME',
  PrimaryInsurancePolicyHolderAge = 'PRIMARY_INSURANCE_POLICY_HOLDER_AGE',
  PrimaryInsurancePolicyHolderDOB = 'PRIMARY_INSURANCE_POLICY_HOLDER_DOB',
  PrimaryInsurancePolicyHolderHomeAddress = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_ADDRESS',
  PrimaryInsurancePolicyHolderHomeCityStateZip = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_CITY_STATE_ZIP',
  PrimaryInsurancePolicyHolderHomePhoneNumber = 'PRIMARY_INSURANCE_POLICY_HOLDER_HOME_PHONE_NUMBER',
  PrimaryInsurancePolicyHolderName = 'PRIMARY_INSURANCE_POLICY_HOLDER_NAME',
  PrimaryInsurancePolicyHolderRelationshipToPatient = 'PRIMARY_INSURANCE_POLICY_HOLDER_RELATIONSHIP_TO_PATIENT',
  PrimaryInsurancePolicyHolderSex = 'PRIMARY_INSURANCE_POLICY_HOLDER_SEX',
  PrimaryInsurancePolicyNumber = 'PRIMARY_INSURANCE_POLICY_NUMBER',

  SecondaryInsuranceGroupNumber = 'SECONDARY_INSURANCE_GROUP_NUMBER',
  SecondaryInsuranceInsuranceName = 'SECONDARY_INSURANCE_INSURANCE_NAME',
  SecondaryInsurancePolicyHolderAge = 'SECONDARY_INSURANCE_POLICY_HOLDER_AGE',
  SecondaryInsurancePolicyHolderDOB = 'SECONDARY_INSURANCE_POLICY_HOLDER_DOB',
  SecondaryInsurancePolicyHolderHomeAddress = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_ADDRESS',
  SecondaryInsurancePolicyHolderHomeCityStateZip = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_CITY_STATE_ZIP',
  SecondaryInsurancePolicyHolderHomePhoneNumber = 'SECONDARY_INSURANCE_POLICY_HOLDER_HOME_PHONE_NUMBER',
  SecondaryInsurancePolicyHolderName = 'SECONDARY_INSURANCE_POLICY_HOLDER_NAME',
  SecondaryInsurancePolicyHolderRelationshipToPatient = 'SECONDARY_INSURANCE_POLICY_HOLDER_RELATIONSHIP_TO_PATIENT',
  SecondaryInsurancePolicyHolderSex = 'SECONDARY_INSURANCE_POLICY_HOLDER_SEX',
  SecondaryInsurancePolicyNumber = 'SECONDARY_INSURANCE_POLICY_NUMBER',

  VisitAdmitDate = 'VISIT_ADMIT_DATE',
  VisitAdmitDatetime = 'VISIT_ADMIT_DATETIME',
  VisitAdmittingPhysicianName = 'VISIT_ADMITTING_PHYSICIAN_NAME',
  VisitAttendingPhysicianName = 'VISIT_ATTENDING_PHYSICIAN_NAME',
  VisitBuildingLocation = 'VISIT_BUILDING_LOCATION',
  VisitEHRID = 'VISIT_EHR_ID',
  VisitEHRType = 'VISIT_EHR_TYPE',
  VisitMedicalService = 'VISIT_MEDICAL_SERVICE',
  VisitReason = 'VISIT_REASON',
}

export enum Font {
  Helvetica = 'Helvetica',
  SignPainterHouseScript = 'SignPainterHouseScript',
  UbuntuR = 'Ubuntu R',
}

export interface Command {
  dataSource: string;
  font: Font;
  fontSize: number;
  fontColorR: number; // 0-255
  fontColorG: number; // 0-255
  fontColorB: number; // 0-255
  pageIndex: number;
  // 0, 0 is bottom left of the PDF page
  pointX: number;
  pointY: number;
  pointRef: PointReference;
  lineWidth?: number;
  mappedValue?: DataSource.Crossmark;
  previsitFormFieldId?: string;
  value?: string;
}

export enum Category {
  ConditionsOfAdmission = 'CONDITIONS_OF_ADMISSION',
  FinancialPolicy = 'FINANCIAL_POLICY',
  FormSummary = 'FORM_SUMMARY',
  HealthInformationExchange = 'HEALTH_INFORMATION_EXCHANGE',
  HealthInformationExchangeAcknowledgment = 'HEALTH_INFORMATION_EXCHANGE_ACKNOWLEDGMENT',
  MedicationPolicy = 'MEDICATION_POLICY',
  NoticeOfAdmission = 'NOTICE_OF_ADMISSION',
  PatientAuthorization = 'PATIENT_AUTHORIZATION',
  PatientChargeNotice = 'PATIENT_CHARGE_NOTICE',
  PatientConsent = 'PATIENT_CONSENT',
  PatientConsentBehavioralHealth = 'PATIENT_CONSENT_BEHAVIORAL_HEALTH',
  PatientConsentEstablishedPatient = 'PATIENT_CONSENT_ESTABLISHED_PATIENT',
  PatientConsentNewPatient = 'PATIENT_CONSENT_NEW_PATIENT',
  PatientConsentRemoteMonitoring = 'PATIENT_CONSENT_REMOTE_MONITORING',
  PatientFeeDisclosure = 'PATIENT_FEE_DISCLOSURE',
  PatientRelease = 'PATIENT_RELEASE',
  PatientSpecialtyConsent = 'PATIENT_SPECIALTY_CONSENT',
  PatientTranscriptionConsent = 'PATIENT_TRANSCRIPTION_CONSENT',
  PrivacyPolicy = 'PRIVACY_POLICY',
  TreatmentConsent = 'TREATMENT_CONSENT',
  VirtualConsent = 'VIRTUAL_CONSENT',
}

export interface BaseDocumentTemplate {
  name: string;
  category: Category;
  commands: Command[];
  gsPath: string;
  deleted: Date | null;
}

export interface DocumentTemplate extends BaseItem, BaseDocumentTemplate {}

export type CreateValues = NullablePartial<BaseDocumentTemplate>;
export type UpdateValues = Partial<BaseDocumentTemplate>;

export interface FindAllOptions
  extends Partial<Pick<DocumentTemplate, 'name' | 'category'>> {
  q?: string;
  includeDeleted?: boolean;
}
